import {
  PublicClientApplication,
  AccountInfo,
  InteractionRequiredAuthError,
  AuthenticationResult,
} from '@azure/msal-browser';
import { msalConfig, loginRequest, graphConfig } from './msalConfig';
import { IUser, TUserRole } from '@/types';
import { consoleLog } from '@/utils/console-log';

/**
 * MSAL Instance - Singleton
 * Ensures only one instance of PublicClientApplication exists
 */
let msalInstance: PublicClientApplication | null = null;

/**
 * In-memory cache for Graph API responses
 * Prevents redundant API calls within the cache duration
 */
let cachedUserData: any = null;
let cachedPhotoUrl: string | null = null;
let userDataCacheTimestamp: number = 0;
let photoCacheTimestamp: number = 0;
let previousPhotoBlobUrl: string | null = null;
let userDataRequestInFlight: Promise<any> | null = null;
let photoRequestInFlight: Promise<string | null> | null = null;
let msalInitPromise: Promise<void> | null = null;
let isMsalInitialized = false;

const USER_DATA_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const PHOTO_CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

/**
 * Clear all cached Graph API data
 * Call this on logout or when fresh data is needed
 */
export const clearGraphCache = (): void => {
  cachedUserData = null;
  cachedPhotoUrl = null;
  userDataCacheTimestamp = 0;
  photoCacheTimestamp = 0;
  userDataRequestInFlight = null;
  photoRequestInFlight = null;
  if (previousPhotoBlobUrl && previousPhotoBlobUrl.startsWith('blob:')) {
    URL.revokeObjectURL(previousPhotoBlobUrl);
  }
  previousPhotoBlobUrl = null;

  // Clear from localStorage
  if (typeof window !== 'undefined') {
    // localStorage.removeItem('userPhotoUrl');
    localStorage.removeItem('userPhotoTimestamp');
  }
};

/**
 * Get or create the MSAL instance (singleton pattern)
 * Ensures only one instance exists across the application
 */
export const getMsalInstance = (): PublicClientApplication => {
  if (!msalInstance) {
    msalInstance = new PublicClientApplication(msalConfig);
  }
  return msalInstance;
};

/**
 * Ensure MSAL is initialized exactly once.
 * This avoids transient "uninitialized_public_client_application" errors when
 * API calls race ahead of auth bootstrap.
 */
const ensureMsalInitialized = async (): Promise<void> => {
  if (isMsalInitialized) {
    return;
  }

  const instance = getMsalInstance();

  if (!msalInitPromise) {
    msalInitPromise = instance
      .initialize()
      .then(() => {
        isMsalInitialized = true;
      })
      .catch((error) => {
        msalInitPromise = null;
        throw error;
      });
  }

  await msalInitPromise;
};

/**
 * Initialize MSAL and handle redirects
 * Call this once when the app starts
 * @returns AuthenticationResult if redirect was successful, null otherwise
 */
export const initializeMsal = async (): Promise<AuthenticationResult | null> => {
  try {
    const instance = getMsalInstance();
    await ensureMsalInitialized();
    return await instance.handleRedirectPromise();
  } catch (error) {
    console.error('Failed to initialize MSAL:', error);
    throw error;
  }
};

/**
 * Get the active account from MSAL
 * Returns the first account if available
 */
export const getActiveAccount = (): AccountInfo | null => {
  const instance = getMsalInstance();
  try {
    const accounts = instance.getAllAccounts();
    if (accounts.length > 0) {
      return accounts[0];
    }
  } catch (error) {
    console.warn('MSAL account lookup skipped before initialization:', error);
  }
  return null;
};

/**
 * Acquire an access token silently
 * Will attempt to use cached tokens or refresh token
 * Falls back to interactive login if silent acquisition fails
 */
export const acquireTokenSilent = async (
  scopes: string[] = loginRequest.scopes,
): Promise<string | null> => {
  let instance: PublicClientApplication;

  try {
    await ensureMsalInitialized();
    instance = getMsalInstance();
  } catch (error) {
    console.warn('MSAL not ready for silent token acquisition:', error);
    return null;
  }

  const account = getActiveAccount();

  if (!account) {
    console.warn('No active account found for silent token acquisition');
    return null;
  }

  try {
    const response = await instance.acquireTokenSilent({
      scopes,
      account,
    });
    return response.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      // Silent token acquisition failed, user interaction required
      console.warn('Silent token acquisition failed, interaction required:', error);
      // Could trigger interactive login here if needed
      // await instance.acquireTokenRedirect({ scopes, account });
    } else {
      console.error('Error acquiring token silently:', error);
    }
    return null;
  }
};

/**
 * Check whether the cached MSAL account currently has a usable session.
 *
 * Unlike `acquireTokenSilent` (which collapses every failure to `null` so
 * call sites like the Axios/Graph helpers stay resilient to transient
 * issues), this distinguishes a *definitive* "no usable session" signal
 * (`InteractionRequiredAuthError`) from any other unexpected error, which is
 * treated as inconclusive/transient and does NOT invalidate the session.
 *
 * A cached account can outlive its actual session - MSAL still returns it
 * from `getAllAccounts()`/localStorage even when no token can ever be issued
 * for it again. Treating "an account object exists" as "authenticated"
 * without this check is what previously caused an infinite login redirect
 * loop: the same dead account was rediscovered and retried on every page
 * load without ever being cleared.
 *
 * Defaults to the login-request scopes rather than the backend API scope: a
 * dead refresh token raises `InteractionRequiredAuthError` for any scope, but
 * the API scope is never requested at login, so a consent/Conditional Access
 * gap on it alone would fail validation forever and re-create the loop.
 */
export const isAccountSessionValid = async (
  scopes: string[] = loginRequest.scopes,
): Promise<boolean> => {
  let instance: PublicClientApplication;

  try {
    await ensureMsalInitialized();
    instance = getMsalInstance();
  } catch (error) {
    console.warn('MSAL not ready to validate session, treating as invalid:', error);
    return false;
  }

  const account = getActiveAccount();

  if (!account) {
    return false;
  }

  try {
    await instance.acquireTokenSilent({ scopes, account });
    return true;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      console.warn('MSAL session validation failed, interaction required:', error);
      return false;
    }

    console.warn('Unexpected error validating MSAL session, treating as valid:', error);
    return true;
  }
};

/**
 * Fetch user data from Microsoft Graph API
 * Uses the /me endpoint to get user profile information
 * Implements in-memory caching to prevent redundant calls
 * @param forceRefresh - Skip cache and fetch fresh data
 */
export const fetchGraphUserData = async (forceRefresh: boolean = false): Promise<any> => {
  try {
    if (!forceRefresh && userDataRequestInFlight) {
      consoleLog.info('Graph user data request already in flight, reusing promise');
      return await userDataRequestInFlight;
    }

    // Return cached data if available and not expired
    if (
      !forceRefresh &&
      cachedUserData &&
      Date.now() - userDataCacheTimestamp < USER_DATA_CACHE_DURATION
    ) {
      consoleLog.info('Using cached user data from Graph API');
      return cachedUserData;
    }

    userDataRequestInFlight = (async () => {
      const token = await acquireTokenSilent(graphConfig.scopes);

      if (!token) {
        throw new Error('Failed to acquire token for Graph API call');
      }

      consoleLog.info('Fetching fresh user data from Graph API');
      const response = await fetch(graphConfig.graphMeEndpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
          cache: 'no-cache',
        },
      });

      if (!response.ok) {
        throw new Error(`Graph API request failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      // Update cache
      cachedUserData = data;
      userDataCacheTimestamp = Date.now();

      return data;
    })();

    return await userDataRequestInFlight;
  } catch (error) {
    console.error('Error fetching user data from Graph API:', error);
    throw error;
  } finally {
    userDataRequestInFlight = null;
  }
};

/**
 * Fetch user profile photo from Microsoft Graph API
 * Returns a blob URL that can be used as an image source
 * Implements caching, blob validation, and memory leak prevention
 * @param forceRefresh - Skip cache and fetch fresh photo
 * @returns Promise<string | null> - Blob URL of the photo or null if not available
 */
export const fetchGraphUserPhoto = async (
  forceRefresh: boolean = false,
): Promise<string | null> => {
  try {
    if (!forceRefresh && photoRequestInFlight) {
      consoleLog.info('Graph photo request already in flight, reusing promise');
      return await photoRequestInFlight;
    }

    // Return cached photo URL if available and not expired
    if (
      !forceRefresh &&
      cachedPhotoUrl &&
      Date.now() - photoCacheTimestamp < PHOTO_CACHE_DURATION
    ) {
      consoleLog.info('Using cached photo URL from Graph API');
      return cachedPhotoUrl;
    }

    // Check localStorage for persisted photo URL
    // if (!forceRefresh && typeof window !== 'undefined') {
    //   const storedPhotoUrl = localStorage.getItem('userPhotoUrl');
    //   const storedPhotoTimestamp = localStorage.getItem('userPhotoTimestamp');

    //   if (storedPhotoUrl && storedPhotoTimestamp) {
    //     const timestamp = parseInt(storedPhotoTimestamp, 10);
    //     if (Date.now() - timestamp < PHOTO_CACHE_DURATION) {
    //       consoleLog.info('Using photo URL from localStorage');
    //       cachedPhotoUrl = storedPhotoUrl;
    //       photoCacheTimestamp = timestamp;
    //       return storedPhotoUrl;
    //     }
    //   }
    // }

    photoRequestInFlight = (async () => {
      const token = await acquireTokenSilent(graphConfig.scopes);
      consoleLog.log('🔑 Token acquired:', token ? 'Yes' : 'No');

      if (!token) {
        console.warn('Failed to acquire token for Graph API photo fetch');
        return null;
      }

      consoleLog.info('Fetching fresh user photo from Graph API');
      const response = await fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
        headers: {
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-store',
        },
      });

      consoleLog.log('📡 Photo fetch response status:', response.status);

      // Handle HTTP 304 Not Modified - content hasn't changed
      // Return existing cached blob URL since the cached version is still valid
      if (response.status === 304) {
        console.info('📷 Photo not modified (304), using cached blob URL');
        if (cachedPhotoUrl) {
          console.info('✅ Returning existing cached photo URL');
          return cachedPhotoUrl;
        }
        console.warn('⚠️ Received 304 but no cached photo URL available');
        return null;
      }

      if (!response.ok) {
        // 404 means user has no profile photo set
        if (response.status === 404) {
          consoleLog.info('ℹ️ User has no profile photo in Microsoft Graph (404)');
          return null;
        }
        // Handle other errors gracefully
        if (response.status === 401 || response.status === 403) {
          console.warn(`Graph API photo access denied: ${response.status}`);
          return null;
        }
        throw new Error(`Graph API photo request failed: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();

      // Validate blob size - reject empty blobs
      if (blob.size === 0) {
        consoleLog.warn('User photo blob is empty (0 bytes), skipping');
        return null;
      }

      // Revoke previous blob URL to prevent memory leaks
      if (previousPhotoBlobUrl && previousPhotoBlobUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previousPhotoBlobUrl);
        consoleLog.info('Revoked previous blob URL to prevent memory leak');
      }

      const blobUrl = URL.createObjectURL(blob);
      previousPhotoBlobUrl = blobUrl;

      // Update cache
      cachedPhotoUrl = blobUrl;
      photoCacheTimestamp = Date.now();

      // Persist to localStorage
      // if (typeof window !== 'undefined') {
      //   localStorage.setItem('userPhotoUrl', blobUrl);
      //   localStorage.setItem('userPhotoTimestamp', photoCacheTimestamp.toString());
      // }

      return blobUrl;
    })();

    return await photoRequestInFlight;
  } catch (error) {
    console.error('Error fetching user photo from Graph API:', error);
    return null;
  } finally {
    photoRequestInFlight = null;
  }
};

/**
 * Map Azure AD user data to application IUser type
 * Extracts relevant fields and applies default role mapping
 *
 * @param account - MSAL AccountInfo from authentication
 * @param graphData - Optional Microsoft Graph user data for additional fields
 * @param photoUrl - Optional photo blob URL from Microsoft Graph
 * @returns IUser object for application use
 */
export const mapAzureUserToAppUser = (
  account: AccountInfo,
  graphData?: any,
  photoUrl?: string | null,
): IUser => {
  // Debug logging: Photo URL
  consoleLog.group('📸 Photo URL Debug');
  consoleLog.log('photoUrl parameter:', photoUrl);
  consoleLog.log('photoUrl type:', typeof photoUrl);
  consoleLog.log('photoUrl is null:', photoUrl === null);
  consoleLog.log('photoUrl is undefined:', photoUrl === undefined);
  consoleLog.log('photoUrl truthiness:', !!photoUrl);
  consoleLog.groupEnd();

  // Debug logging: Log all properties on account object
  consoleLog.group('🔍 Azure AD Account Properties');
  consoleLog.log('Full account object:', account);
  consoleLog.log('account.localAccountId:', account.localAccountId);
  consoleLog.log('account.homeAccountId:', account.homeAccountId);
  consoleLog.log('account.username:', account.username);
  consoleLog.log('account.name:', account.name);
  consoleLog.log('account.environment:', account.environment);
  consoleLog.log('account.tenantId:', account.tenantId);
  consoleLog.log('account.idTokenClaims:', account.idTokenClaims);
  consoleLog.log('All account keys:', Object.keys(account));
  consoleLog.groupEnd();

  // Debug logging: Log all properties on graphData object
  consoleLog.group('🔍 Microsoft Graph Data Properties');
  if (graphData) {
    consoleLog.log('Full graphData object:', graphData);
    consoleLog.log('graphData.displayName:', graphData.displayName);
    consoleLog.log('graphData.mail:', graphData.mail);
    consoleLog.log('graphData.userPrincipalName:', graphData.userPrincipalName);
    consoleLog.log('graphData.department:', graphData.department);
    consoleLog.log('graphData.jobTitle:', graphData.jobTitle);
    consoleLog.log('graphData.officeLocation:', graphData.officeLocation);
    consoleLog.log('graphData.mobilePhone:', graphData.mobilePhone);
    consoleLog.log('All graphData keys:', Object.keys(graphData));
  } else {
    consoleLog.log('graphData is undefined or null');
  }
  consoleLog.groupEnd();

  // Extract role from token claims or default to 'rep'
  // You can customize this based on your Azure AD groups or custom claims
  let role: TUserRole = 'rep';

  // Example: Check for admin role in ID token claims
  if (account.idTokenClaims?.roles?.includes('FieldOne.Admin')) {
    role = 'maintainer';
  }

  // Example: Check for group membership (requires groups claim in token)
  if (
    Array.isArray(account.idTokenClaims?.groups) &&
    account.idTokenClaims.groups.includes('manager-group-id')
  ) {
    role = 'maintainer';
  }

  const usernameFromEmail = account.username?.split('@')?.[0]?.toLowerCase();

  const user: IUser = {
    certifications: [],
    createdAt: new Date(),
    department: graphData?.department,
    email: account.username || graphData?.mail || graphData?.userPrincipalName,
    imageUrl: photoUrl || undefined,
    name: account.name || graphData?.displayName || 'Unknown User',
    role,
    updatedAt: new Date(),
    username: usernameFromEmail || account.localAccountId || account.homeAccountId,
    uuid: account.localAccountId || account.homeAccountId,
  };

  // Persist photo URL to localStorage for future sessions
  // if (photoUrl && typeof window !== 'undefined') {
  //   localStorage.setItem('userPhotoUrl', photoUrl);
  //   localStorage.setItem('userPhotoTimestamp', Date.now().toString());
  // }

  consoleLog.group('✅ Mapped IUser Object');
  consoleLog.log('Mapped user:', user);
  consoleLog.groupEnd();

  return user;
};

/**
 * Sign out the current user
 * Clears MSAL cache and redirects to logout endpoint
 */
export const signOut = async (): Promise<void> => {
  const instance = getMsalInstance();
  const account = getActiveAccount();

  if (account) {
    await instance.logoutRedirect({
      account,
    });
  } else {
    await instance.logoutRedirect();
  }
};

/**
 * Check if user is authenticated
 * Returns true if there's an active account
 */
export const isUserAuthenticated = (): boolean => {
  return getActiveAccount() !== null;
};
